import React, { useMemo, useState, useRef, useEffect, createRef } from 'react';
import type { PageProps } from '../types';
import { useAuditStore } from '../stores/auditStore';
import { AffectedDisabilities, type AffectedDisability } from '../components/AffectedDisabilities';
import { FormattedIssueText } from '../components/FormattedIssueText';
import { IssueNotFoundInMobile } from '../components/icons/IssueNotFoundInMobile';
import { IssueNotFoundInDesktop } from '../components/icons/IssueNotFoundInDesktop';

export const ElementListingPage: React.FC<PageProps> = ({ onNavigate }) => {
  const { 
    selectedIssueId, 
    selectedIssueCategory,
    auditResult, 
    selectedDevice, 
    setIssueElement,
    isDashboardVisible,
    selectedElementIndex,
    setSelectedElementIndex 
  } = useAuditStore();

  const currentIssue = useMemo(() => {
    try {
      const data = (auditResult as any)?.body?.data?.report_data || {};
      const list: any[] = selectedIssueCategory === 'best-practice'
        ? (Array.isArray(data.best_practices?.[selectedDevice]) ? data.best_practices[selectedDevice] : [])
        : (Array.isArray((data as any)[selectedDevice]) ? (data as any)[selectedDevice] : []);
      if (selectedIssueId) {
        const found = list.find((it: any) => String(it?.issue_id) === String(selectedIssueId));
        return found;
      }
      return false;
    } catch {
      return false;
    }
  }, [auditResult, selectedDevice, selectedIssueId, selectedIssueCategory]);

  const items: any[] = useMemo(() => {
    if (!currentIssue) return [];
    const detailItems = currentIssue?.issue_content_json?.details?.items;
    const helpData = currentIssue?.issue_content_json?.help ||
                     currentIssue?.issue_content_json?.dev_description ||
                     currentIssue?.audit_data_json?.help ||
                     currentIssue?.audit_data_json?.dev_description ||
                     currentIssue?.issue_content_json?.description ||
                     currentIssue?.audit_data_json?.description ||
                     'Issue detected on this element';
    
    if (Array.isArray(detailItems)) {
      return detailItems.map(item => ({
        ...item,
        help: helpData
      }));
    }
    return [];
  }, [currentIssue]);

  const wcagString = useMemo(() => {
    if (!currentIssue) return '';
    const v = currentIssue?.wcag_version_number;
    const level = (currentIssue?.wcag_level || '').toUpperCase();
    const ver = v ? (String(v).includes('.') ? String(v) : `${v}.0`) : '';
    const sc = currentIssue?.standard_code || currentIssue?.issue_content_json?.standardCode || '';
    const parts = [] as string[];
    if (ver) parts.push(`WCAG ${ver}`);
    if (level) parts.push(level);
    if (sc) parts.push(sc);
    return parts.join(' ');
  }, [currentIssue]);

  const affectedDisabilities = useMemo(() => {
    if (!currentIssue) return [];
    
    const disabilitiesData = currentIssue?.affected_disabilities_json;
    
    if (Array.isArray(disabilitiesData)) {
      return disabilitiesData as AffectedDisability[];
    }
    
    if (typeof disabilitiesData === 'string') {
      try {
        const parsed = JSON.parse(disabilitiesData);
        if (Array.isArray(parsed)) {
          return parsed as AffectedDisability[];
        }
      } catch {
        return disabilitiesData
          .split(/[,|;]/)
          .map(d => d.trim())
          .filter(Boolean) as AffectedDisability[];
      }
    }

    return [];
  }, [currentIssue]);

  const title = currentIssue?.issue_title || currentIssue?.issue_id || 'Issue details';

  const severity = String(currentIssue?.issue_severity || '').toLowerCase();
  const severityLabel = severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : '';
  const severityClasses = (() => {
    switch (severity) {
      case 'critical':
        return 'cy:bg-red-100 cy:text-red-700 cy:border cy:border-red-200';
      case 'serious':
        return 'cy:bg-orange-100 cy:text-orange-700 cy:border cy:border-orange-200';
      case 'moderate':
      case 'medium':
        return 'cy:bg-amber-100 cy:text-amber-700 cy:border cy:border-amber-200';
      case 'minor':
        return 'cy:bg-gray-100 cy:text-gray-700 cy:border cy:border-gray-200';
      default:
        return 'cy:bg-gray-100 cy:text-gray-700 cy:border cy:border-gray-200';
    }
  })();
  const description =
    currentIssue?.issue_content_json?.dev_description ||
    currentIssue?.audit_data_json?.dev_description ||
    '';
  const [expanded, setExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const elementRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);
  
  // Use expandedElements as derived state from selectedElementIndex
  const expandedElements = useMemo(() => {
    return selectedElementIndex !== null ? new Set([selectedElementIndex]) : new Set<number>();
  }, [selectedElementIndex]);

  useEffect(() => {
    const compute = () => {
      if (!description) { setCanToggle(false); return; }
      const cont = cardRef.current;
      const meas = measureRef.current;
      if (!cont || !meas) return;
      const width = cont.clientWidth || 0;
      meas.style.width = width ? `${width}px` : 'auto';
      meas.textContent = description;
      const cs = window.getComputedStyle(meas);
      const lineHeight = parseFloat(cs.lineHeight) || 20;
      const fullHeight = meas.scrollHeight;
      const maxHeight = lineHeight * 3;
      setCanToggle(fullHeight > maxHeight + 1);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [description, selectedDevice]);

  // Handle dashboard visibility changes - clear/restore markers
  useEffect(() => {
    const utils = (window as any).webyesHighlightUtils;
    
    if (!isDashboardVisible) {
      // Dashboard is hidden - clear all markers and borders
      try {
        utils?.clearMarkers?.();
        utils?.removeElementBorder?.();
        utils?.clearSelection?.();
      } catch (e) {
        console.warn('Error clearing markers on hide:', e);
      }
      return;
    }

    // Dashboard is visible - restore markers if we have items
    if (!currentIssue || items.length === 0) {
      return;
    }

    
    // Small delay to ensure DOM is ready
    const markTimeout = setTimeout(() => {
      try {
        if (utils?.markIssueItems) {
          utils.markIssueItems(items, selectedIssueCategory ?? 'wcag');
          
          // If there's a selected element, restore its highlighting
          if (selectedElementIndex !== null && items[selectedElementIndex]) {
            const item = items[selectedElementIndex];
            const selector = item.target?.[0];
            if (selector) {
              utils?.addElementBorder?.(selector);
              utils?.setSelection?.(selectedElementIndex);
              scrollToElementInIframe(selector);
            }
          }
        } else {
          console.warn('[React] webyesHighlightUtils not available');
        }
      } catch (e) {
        console.warn('Error marking items:', e);
      }
    }, 100);

    return () => {
      clearTimeout(markTimeout);
    };
  }, [isDashboardVisible, currentIssue, items, selectedElementIndex, selectedIssueCategory]);

  // Cleanup on unmount or device change
  useEffect(() => {
    return () => {
      try {
        const utils = (window as any).webyesHighlightUtils;
        utils?.clearMarkers?.();
        utils?.removeElementBorder?.();
      } catch (e) {
        console.warn('Error cleaning up markers:', e);
      }
    };
  }, [selectedDevice]);

  // Initialize refs array for each item
  useEffect(() => {
    elementRefs.current = items.map((_, i) => elementRefs.current[i] || createRef());
  }, [items]);

  // Scroll to selected element when expandedElements changes
  useEffect(() => {
    if (expandedElements.size === 0) return;
    
    const expandedIndex = Array.from(expandedElements)[0];
    const elementRef = elementRefs.current[expandedIndex];
    
    if (elementRef?.current) {
      // Small delay to ensure the element is rendered with its expanded state
      setTimeout(() => {
        elementRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [expandedElements]);

  // Listen for marker click/deselect messages from content script
  useEffect(() => {
    const handleMarkerMessage = (event: MessageEvent) => {
      if (event.data?.source !== 'WEBYES') return;
      
      // Handle marker click (select)
      if (event.data?.type === 'ISSUE_MARKER_CLICK') {
        const clickedIndex = event.data.index;
        
        // Expand and highlight the clicked element in the list
        setSelectedElementIndex(clickedIndex);
        setIssueElement(event.data.selector);
      }
      
      // Handle marker deselect (toggle off)
      if (event.data?.type === 'ISSUE_MARKER_DESELECT') {
        
        // Clear selection in list
        setSelectedElementIndex(null);
        setIssueElement(null);
      }
    };

    window.addEventListener('message', handleMarkerMessage);
    return () => window.removeEventListener('message', handleMarkerMessage);
  }, [setIssueElement, setSelectedElementIndex]);

  const toggleElementExpansion = (item: {target: string[]; node: {selector: string; boundingRect: {x: number; y: number; width: number; height: number}; snippet: string; html: string}}, index: number) => {
    const selector = item.target?.[0];
    const utils = (window as any).webyesHighlightUtils;
    
    const isCurrentlyExpanded = selectedElementIndex === index;
    
    if (isCurrentlyExpanded) {
      // Collapsing/Deselecting
      setSelectedElementIndex(null);
      setIssueElement(null);
      
      try { 
        // Remove border
        utils?.removeElementBorder?.();
        // Clear selection in content script
        utils?.clearSelection?.();
      } catch (error) {
        console.warn('Error removing border:', error);
      }
    } else {
      // Expanding/Selecting
      setSelectedElementIndex(index);
      setIssueElement(selector);
      
      try {
        if (selector) {
          // Add red border to element
          utils?.addElementBorder?.(selector);
          // Auto-scroll to the element in the page content
          scrollToElementInIframe(selector);
          // Update selection in content script
          utils?.setSelection?.(index);
        }
      } catch (error) {
        console.warn('Error in element expansion:', error);
      }
    }
  };

  const scrollToElementInIframe = (selector: string) => {
    try {
      const utils = (window as any).webyesHighlightUtils;
      if (utils?.scrollToElementInIframe) {
        utils.scrollToElementInIframe(selector);
      }
    } catch (error) {
      console.warn('Error scrolling to element in iframe:', error);
    }
  };
  useEffect(() => {
    return () => {
      setIssueElement(null);
    };
  }, [setIssueElement]);

  useEffect(() => {
    setIssueElement(null);
    setSelectedElementIndex(null);
  }, [selectedDevice, setIssueElement, setSelectedElementIndex]);

  if(!currentIssue) {
    return (
      <div className="cy:flex cy:flex-col cy:items-center cy:justify-center cy:py-12 cy:px-4">
        {/* Show appropriate icon based on device */}
        {selectedDevice === 'mobile' ? (
          <IssueNotFoundInMobile width={150} height={150} />
        ) : (
          <IssueNotFoundInDesktop width={150} height={150} />
        )}
        
        <h3 className="cy:mt-4 cy:text-lg cy:font-semibold cy:text-gray-900">
          Affected element not found on {selectedDevice}
        </h3>
        <p className="cy:mt-2 cy:text-sm cy:text-gray-600 cy:text-center">
          The element flagged in this issue is not found on the {selectedDevice} version of the page.<br/>
          {selectedDevice === 'desktop' ? 'Be sure to review the mobile version for the flagged element.' : 'Be sure to review the desktop version for the flagged element.'}
        </p>
        <button
          className="cy:mt-4 cy:bg-blue-600 cy:text-white cy:px-4 cy:py-2 cy:rounded cy:hover:bg-blue-700 cy:border-0 cy:cursor-pointer"
          onClick={() => onNavigate('accessibilityIssueHeading')}
        >
          Back to issues
        </button>
      </div>
    );
  }

  return (
    <div className="cy:flex cy:flex-col cy:gap-3">
      {/* Issue Title block (white card) */}
      <div ref={cardRef} className="cy:bg-white cy:border cy:border-gray-200 cy:p-3">
        <div className="cy:text-lg cy:font-semibold cy:text-gray-900">{title}</div>
        <div className="cy:mt-1 cy:flex cy:flex-row cy:flex-wrap cy:items-center cy:gap-2">
          {wcagString ? (
            <div className="cy:text-xs cy:text-gray-600">{wcagString}</div>
          ) : null}
          {severityLabel && (
            <span className={`cy:text-xs cy:px-2 cy:py-0.5 cy:rounded ${severityClasses}`}>{severityLabel}</span>
          )}
        </div>

        {/* Affected disabilities icons */}
        {affectedDisabilities.length > 0 && (
          <div className="cy:mt-1">
            <AffectedDisabilities 
              disabilities={affectedDisabilities}
              className="cy:text-gray-600"
              iconSize={20}
            />
          </div>
        )}

        {/* Description with show more/less (plain text or simple markdown: **bold**, `code`, newlines) */}
        {description && (
          <div className="cy:mt-2">
            <FormattedIssueText
              text={description}
              as="p"
              className="cy:text-sm cy:text-gray-800"
              style={expanded ? undefined : {
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              } as React.CSSProperties}
            />
            {canToggle && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="cy:mt-1 cy:text-sm cy:text-blue-600 cy:hover:underline cy:font-medium cy:border-0 cy:bg-transparent cy:cursor-pointer cy:p-0"
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
            {/* Hidden measurement element */}
            <div
              ref={measureRef}
              className="cy:absolute cy:invisible cy:pointer-events-none cy:whitespace-normal cy:text-sm"
              style={{ left: -99999, top: -99999, position: 'absolute' as any }}
            />
          </div>
        )}
      </div>

      {/* Affected elements */}
      <div className="cy:mt-3 cy:px-4">
        <div className="cy:text-sm cy:font-semibold cy:text-gray-800 cy:mb-2">Affected elements ({items.length})</div>
        <div className="cy:space-y-2">
          {items.map((item, idx) => {
            const isExpanded = expandedElements.has(idx);
            const snippet = item?.target?.[0] || item?.node?.snippet || item?.node?.html || 'Unknown selector';
            
            return (
              <div 
                key={idx}
                ref={elementRefs.current[idx]}
                className={`cy:bg-white cy:border cy:rounded-lg cy:overflow-hidden cy:px-2 cy:transition-all ${
                  isExpanded 
                    ? 'cy:border-blue-500 cy:border-2 cy:shadow-md' 
                    : 'cy:border-gray-200'
                }`}
              >
                {/* Main element row - clickable to expand */}
                <div 
                  className="cy:p-3 cy:flex cy:items-center cy:justify-between cy:cursor-pointer cy:hover:bg-gray-50"
                  onClick={() => toggleElementExpansion(item, idx)}
                >
                  <div className="cy:flex cy:items-start cy:gap-3 cy:pr-2 cy:min-w-0 cy:flex-1">
                    <div className="cy:h-2.5 cy:w-2.5 cy:flex-shrink-0">
                      <span
                        className={`cy:mt-1 cy:inline-block cy:w-2.5 cy:h-2.5 cy:rounded-full ${
                          selectedIssueCategory === 'best-practice' ? 'cy:bg-amber-500' : 'cy:bg-red-500'
                        }`}
                        aria-hidden
                      />
                    </div>
                    <div className={`cy:text-sm cy:text-gray-800 cy:min-w-0 ${isExpanded ? 'cy:break-all' : 'cy:truncate'}`} title={snippet}>
                      {snippet}
                    </div>
                  </div>
                  <svg 
                    className={`cy:w-4 cy:h-4 cy:text-gray-400 cy:transition-transform cy:flex-shrink-0 ${isExpanded ? 'cy:rotate-90' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    height={21}
                    width={21}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

